import { useEffect, useRef, useState } from 'react';

interface GoogleSignInButtonProps {
  onCredential: (credential: string) => void;
  isLoading?: boolean;
}

const GOOGLE_SCRIPT_ID = 'google-identity-services';

function loadGoogleIdentityScript(): Promise<void> {
  if (window.google?.accounts.id) {
    return Promise.resolve();
  }

  const existingScript = document.getElementById(GOOGLE_SCRIPT_ID) as HTMLScriptElement | null;
  if (existingScript) {
    return new Promise((resolve, reject) => {
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener('error', () => reject(new Error('Google Identity Services failed to load')), { once: true });
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = GOOGLE_SCRIPT_ID;
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Google Identity Services failed to load'));
    document.head.appendChild(script);
  });
}

export default function GoogleSignInButton({ onCredential, isLoading = false }: GoogleSignInButtonProps) {
  const buttonRef = useRef<HTMLDivElement>(null);
  const callbackRef = useRef(onCredential);
  const [loadError, setLoadError] = useState<string | null>(null);
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  useEffect(() => {
    callbackRef.current = onCredential;
  }, [onCredential]);

  useEffect(() => {
    if (!clientId || !buttonRef.current) return;

    let active = true;
    loadGoogleIdentityScript()
      .then(() => {
        if (!active || !buttonRef.current || !window.google) return;
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: (response) => callbackRef.current(response.credential),
          auto_select: false,
          cancel_on_tap_outside: true,
        });
        buttonRef.current.replaceChildren();
        const availableWidth = Math.floor(buttonRef.current.getBoundingClientRect().width);
        window.google.accounts.id.renderButton(buttonRef.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'continue_with',
          shape: 'pill',
          logo_alignment: 'left',
          width: Math.min(320, availableWidth),
        });
      })
      .catch(() => {
        if (active) setLoadError('Google sign-in could not be loaded. Check your connection and try again.');
      });

    return () => {
      active = false;
    };
  }, [clientId]);

  if (!clientId) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" role="status">
        Google sign-in needs a client ID before this environment can accept accounts.
      </div>
    );
  }

  if (loadError) {
    return <p className="text-sm text-red-600" role="alert">{loadError}</p>;
  }

  return (
    <div className="relative flex min-h-[44px] justify-center">
      <div ref={buttonRef} aria-label="Continue with Google" />
      {isLoading && <div className="absolute inset-0 cursor-wait rounded-full bg-white/70" aria-hidden="true" />}
    </div>
  );
}
